import { useEffect, useState } from 'react';
import { workOrdersApiKeyStore } from '../../../../api/workorders-api-key-store';
import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { columns } from './workorders-grid-columns';
import { State } from '@progress/kendo-data-query';
import { GridColumnConfig, GridPreference } from '../../../../models/gridCoumnConfig';
import { useLoadData } from '../../../../hooks/useLoadData';
import { gridApiKeyStore } from '../../../../api/grid-api-key-store';
import { ApiResponse } from '../../../../models/api-response';
import { WorkOrder } from '../../../../models/workOrder';
import { useSaveData } from '../../../../hooks/useSaveData';
import useDebounce from '../../../../hooks/use-debounce';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface CustomGridCellProps {
  field: string;
}

interface CellProps {
  workorder: any;
}

interface WorkOrdersTabProps {
  projectID: number;
  customerID?: number;

  officeID?: number;
}

export const WorkOrdersTab = ({ projectID, customerID, officeID }: WorkOrdersTabProps) => {

  const navigate = useNavigate();
  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Project Details', 'Workorder'));

  const {
    isLoading,
    data: workorder,
    refetch,
  } = useLoadData<ApiResponse<WorkOrder[]>>(workOrdersApiKeyStore.getWorkOrders({ projectID, customerID }));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredWorkorders, setFilteredWorkorders] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredWorkorderList = workorder?.data.filter((plan) =>
      Object.values(plan).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredWorkorders(filteredWorkorderList || []);
  }, [debouncedSearchTerm, workorder?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Workorder',
      pageName: 'Project Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Workorder',
      pageName: 'Project Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  const JobsiteCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleClickJobsite = () => {
      // navigate(`/units?searchType=jobsites&searchText=${workorder?.jobsite}`);
      window.open(`/units/profile/${workorder?.unitID}`, "_blank");
    };
    return (
      <td style={{ cursor: 'pointer' }} onClick={handleClickJobsite}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.jobsite}
        </Typography>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'jobsite') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <JobsiteCell workorder={dataItem.dataItem} />;
      };
    }
    return undefined;
  };

  return (
    <>
      <SiTrackGrid
        fileTitle='project_workorder'
        columns={columns}
        dataItemKey="workOrderID"
        // queryConfig={workOrdersApiKeyStore.getWorkOrders({ projectID, customerID })}
        viewRoute={(value) =>
          `/workorders?customerID=${customerID}&officeID=${officeID}&projectID=${projectID}&unitID=${value?.unitID}&workOrderID=${value?.workOrderID}`
        }
        customCell={(field) =>
          CustomGridCell({
            field,
          })
        }
        actionPermissions={{
          view: UserPermissionName.VIEW_WORKORDER,
          export: UserPermissionName.DOWNLOAD_GRID,
        }}
        dataItems={filteredWorkorders || []}
        reorderable
        handleSearch={handleInputChange}
        handleSaveLayout={handleSaveLayout}
        isSaveLayout={true}
        gridConfigFromApi={gridConfig}
        handleResetLayout={handleResetLayout}
      // -----------------Grid Layout-------
      />
    </>
  );
};
